import {
  ActivityCreationTypeIdEnum,
  AssignmentTagsEnum,
  PredesignedActivityTagsEnum,
  ReplacementQuestionTypeEnum,
  SectionGenerationModeEnum,
  SectionNamingEnum,
  SectionOrderingEnum,
  TestTagsEnum,
} from "@/enums/activity";
import { ActivityKinds, activityKindKey } from "@/constants/activity";
import { ComponentColorType } from "@/types/componentType";

export interface ActivityTypeConfig {
  HasMarking: boolean;
  HasDuration: boolean;
  ConceptsSelectable: boolean;
  TagOptions: object | null;
  IsUniversal: boolean;
  CreationTypeId: number | null;
  DefaultLockReportsTillExpiry: boolean;
}

// Keyed by activityKindKey(classification) — the (Type, Scope, Category) combination.
export const ActivityTypeConfigs: Record<string, ActivityTypeConfig> = {
  [activityKindKey(ActivityKinds.InstituteTest)]: {
    HasMarking: true,
    HasDuration: true,
    ConceptsSelectable: false,
    TagOptions: TestTagsEnum,
    IsUniversal: false,
    CreationTypeId: null,
    DefaultLockReportsTillExpiry: true,
  },
  [activityKindKey(ActivityKinds.Assignment)]: {
    HasMarking: false,
    HasDuration: false,
    ConceptsSelectable: true,
    TagOptions: AssignmentTagsEnum,
    IsUniversal: false,
    CreationTypeId: null,
    DefaultLockReportsTillExpiry: false,
  },
  [activityKindKey(ActivityKinds.StudentPreloadedTest)]: {
    HasMarking: true,
    HasDuration: true,
    ConceptsSelectable: false,
    TagOptions: PredesignedActivityTagsEnum,
    IsUniversal: true,
    CreationTypeId: ActivityCreationTypeIdEnum.PRELOADED,
    DefaultLockReportsTillExpiry: true,
  },
  [activityKindKey(ActivityKinds.InstitutePreloadedTest)]: {
    HasMarking: true,
    HasDuration: true,
    ConceptsSelectable: false,
    TagOptions: PredesignedActivityTagsEnum,
    IsUniversal: true,
    CreationTypeId: ActivityCreationTypeIdEnum.PRELOADED,
    DefaultLockReportsTillExpiry: true,
  },
  [activityKindKey(ActivityKinds.InternalPreloadedTest)]: {
    HasMarking: true,
    HasDuration: true,
    ConceptsSelectable: false,
    TagOptions: PredesignedActivityTagsEnum,
    IsUniversal: true,
    CreationTypeId: ActivityCreationTypeIdEnum.PRELOADED,
    DefaultLockReportsTillExpiry: true,
  },
  [activityKindKey(ActivityKinds.PastYearPaper)]: {
    HasMarking: true,
    HasDuration: true,
    ConceptsSelectable: false,
    TagOptions: null,
    IsUniversal: true,
    CreationTypeId: ActivityCreationTypeIdEnum.PAST_YEAR,
    DefaultLockReportsTillExpiry: false,
  },
  [activityKindKey(ActivityKinds.StudentTestSeries)]: {
    HasMarking: true,
    HasDuration: true,
    ConceptsSelectable: false,
    TagOptions: TestTagsEnum,
    IsUniversal: true,
    CreationTypeId: ActivityCreationTypeIdEnum.PRELOADED,
    DefaultLockReportsTillExpiry: false,
  },
  [activityKindKey(ActivityKinds.InstituteTestSeries)]: {
    HasMarking: true,
    HasDuration: true,
    ConceptsSelectable: false,
    TagOptions: TestTagsEnum,
    IsUniversal: true,
    CreationTypeId: ActivityCreationTypeIdEnum.PRELOADED,
    DefaultLockReportsTillExpiry: true,
  },
  [activityKindKey(ActivityKinds.StudentPreloadedAssignment)]: {
    HasMarking: false,
    HasDuration: false,
    ConceptsSelectable: true,
    TagOptions: PredesignedActivityTagsEnum,
    IsUniversal: true,
    CreationTypeId: ActivityCreationTypeIdEnum.PRELOADED,
    DefaultLockReportsTillExpiry: false,
  },
  [activityKindKey(ActivityKinds.InstitutePreloadedAssignment)]: {
    HasMarking: false,
    HasDuration: false,
    ConceptsSelectable: true,
    TagOptions: null,
    IsUniversal: true,
    CreationTypeId: ActivityCreationTypeIdEnum.PRELOADED,
    DefaultLockReportsTillExpiry: false,
  },
  [activityKindKey(ActivityKinds.InternalPreloadedAssignment)]: {
    HasMarking: false,
    HasDuration: false,
    ConceptsSelectable: true,
    TagOptions: null,
    IsUniversal: true,
    CreationTypeId: ActivityCreationTypeIdEnum.PRELOADED,
    DefaultLockReportsTillExpiry: false,
  },
};

export const ActivitySectionLimits = Object.freeze({
  TotalQuestions: { min: 1, max: 200 },
  MarksPerQuestion: { min: 1, max: 10 },
  NegativeMarks: { min: -10, max: 0 },
});

export const DefaultActivitySectionConfig = Object.freeze({
  QuestionTypeId: 0,
  TotalQuestions: 10,
  AllowedAttempts: 10,
  MarksPerQuestion: 1,
  NegativeMarks: 0,
});

export const MaxActivitySectionNameLength = 20;

export const ActivityDurationOptions = [30, 60, 90, 120, 180];
export const ActivityDurationMin = 10;
export const ActivityDurationMax = 300;
export const ActivityDurationStep = 5;
export const MinimumQuestionTypeCount = 20;

export const SectionGenerationOptions = Object.freeze([
  {
    Id: SectionGenerationModeEnum.SUBJECT_QUESTION_TYPE,
    Name: "Subject x Question Type",
    Description: "One section for each subject + question type",
    Icon: "ti-asterisk",
  },
  {
    Id: SectionGenerationModeEnum.ONLY_SUBJECT,
    Name: "Only Subject",
    Description: "Single section for each subject",
    Icon: "ti-book",
  },
  {
    Id: SectionGenerationModeEnum.ONLY_QUESTION_TYPE,
    Name: "Only Question Type",
    Description: "Single section for each question type",
    Icon: "ti-list-check",
  },
]);

// Only used when SectionGenerationMode is Subject x Question Type
export const SectionOrderingOptions = Object.freeze([
  {
    Id: SectionOrderingEnum.SUBJECT_QUESTION_TYPE,
    Name: "Subject > Question Type",
    Description: "Group by subject first, then question types within each subject",
  },
  {
    Id: SectionOrderingEnum.QUESTION_TYPE_SUBJECT,
    Name: "Question Type > Subject",
    Description: "Group by question type first, then subjects within each type",
  },
]);

const SectionNamingSubjectQType = {
  Id: SectionNamingEnum.SUBJECT_QUESTION_TYPE,
  Name: "{Subject} - {QType}",
  Description: "Physics - MCQ",
};
const SectionNamingSubjectLetter = {
  Id: SectionNamingEnum.SUBJECT_LETTER,
  Name: "{Subject} - A/B/C",
  Description: "Physics - A",
};
const SectionNamingQTypeLetter = {
  Id: SectionNamingEnum.QUESTION_TYPE_LETTER,
  Name: "{QType} - A/B/C",
  Description: "MCQ - A",
};
const SectionNamingSubject = {
  Id: SectionNamingEnum.SUBJECT,
  Name: "{Subject}",
  Description: "Physics",
};
const SectionNamingQType = {
  Id: SectionNamingEnum.QUESTION_TYPE,
  Name: "{QType}",
  Description: "MCQ",
};
const SectionNamingSectionLetter = {
  Id: SectionNamingEnum.SECTION_LETTER,
  Name: "Section A/B/C",
  Description: "Section A",
};
const SectionNamingSectionNumber = {
  Id: SectionNamingEnum.SECTION_NUMBER,
  Name: "Section 1/2/3",
  Description: "Section 1",
};

export const SectionNamingOptions: Record<
  SectionGenerationModeEnum,
  Array<{ Id: SectionNamingEnum; Name: string; Description: string }>
> = {
  [SectionGenerationModeEnum.SUBJECT_QUESTION_TYPE]: [
    SectionNamingSubjectQType,
    SectionNamingSubjectLetter,
    SectionNamingQTypeLetter,
    SectionNamingSectionLetter,
    SectionNamingSectionNumber,
  ],
  [SectionGenerationModeEnum.ONLY_SUBJECT]: [
    SectionNamingSubject,
    SectionNamingSectionLetter,
    SectionNamingSectionNumber,
  ],
  [SectionGenerationModeEnum.ONLY_QUESTION_TYPE]: [
    SectionNamingQType,
    SectionNamingSectionLetter,
    SectionNamingSectionNumber,
  ],
};

export const ReplacementQuestionOptions: ReadonlyArray<{
  Type: ReplacementQuestionTypeEnum;
  Icon: string;
  Color: ComponentColorType;
}> = Object.freeze([
  {
    Type: ReplacementQuestionTypeEnum.EASIER,
    Icon: "chevrons-down",
    Color: "success",
  },
  {
    Type: ReplacementQuestionTypeEnum.HARDER,
    Icon: "chevrons-up",
    Color: "danger",
  },
  {
    Type: ReplacementQuestionTypeEnum.SIMILAR,
    Icon: "congruent-to",
    Color: "primary",
  },
]);
